import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";

interface EmailAccount {
  id: string;
  name: string;
  email: string;
  provider: string;
}

interface EditEmailAccountDialogProps {
  account: EmailAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditEmailAccountDialog = ({ account, open, onOpenChange }: EditEmailAccountDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [accountName, setAccountName] = useState(account.name);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('email_accounts')
        .update({ name: accountName })
        .eq('id', account.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
      onOpenChange(false);
      
      toast({
        title: "Account Updated",
        description: "Email account name has been updated successfully",
      });
    } catch (error) {
      console.error('Error updating account:', error);
      toast({
        title: "Error",
        description: "Failed to update email account",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Email Account</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="accountName">Account Name</Label>
            <Input
              id="accountName"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="My Work Email"
            />
          </div>
          <div>
            <Label>Email Address</Label>
            <Input value={account.email} disabled />
          </div>
          <div>
            <Label>Provider</Label>
            <Input value={account.provider} disabled className="capitalize" />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Account"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditEmailAccountDialog;